import React from 'react';
import moment from 'moment';

const CURRENCY = process.env.REACT_APP_CURRENCY || 'AED';

const fmtDate = (d) => (d ? moment(d).format('DD MMM YYYY') : null);
const fmtDateTime = (d) => (d ? moment(d).format('DD MMM YYYY, hh:mm A') : null);
const fmtMoney = (v) => `${CURRENCY} ${parseFloat(v || 0).toFixed(2)}`;

// Age in whole years, shown next to the date of birth.
const age = (dob) => {
  if (!dob) return null;
  const years = moment().diff(moment(dob), 'years');
  return Number.isFinite(years) && years >= 0 ? `${years} yrs` : null;
};

const Field = ({ label, value, badge, col = 'col-md-6 col-xl-4' }) => (
  <div className={`${col} mb-6`}>
    <label className="text-gray-500 fw-semibold fs-8 text-uppercase mb-1 d-block">{label}</label>
    {badge ? (
      badge
    ) : (
      <div className="text-gray-800 fw-semibold fs-6" style={{ wordBreak: 'break-word' }}>
        {value === 0 || value ? value : <span className="text-gray-400">N/A</span>}
      </div>
    )}
  </div>
);

const Card = ({ title, icon, children, toolbar }) => (
  <div className="card mb-5">
    <div className="card-header border-0 pt-6 min-h-auto pb-2">
      <h3 className="card-title fw-bold m-0 d-flex align-items-center gap-2 fs-5">
        <i className={`bi ${icon}`} style={{ color: '#4F97D8' }}></i>
        {title}
      </h3>
      {toolbar && <div className="card-toolbar">{toolbar}</div>}
    </div>
    <div className="card-body pt-4">{children}</div>
  </div>
);

const StatTile = ({ label, value, icon, color }) => (
  <div className="col-6 col-lg-3">
    <div className="customer-stat-tile">
      <div className="customer-stat-icon" style={{ background: `${color}1A`, color }}>
        <i className={`bi ${icon}`}></i>
      </div>
      <div className="min-w-0">
        <div className="customer-stat-value">{value}</div>
        <div className="customer-stat-label">{label}</div>
      </div>
    </div>
  </div>
);

const ProfileInfo = ({ user }) => {
  if (!user) return <div>Loading...</div>;

  const stats = user.booking_stats || {};
  const membership = user.membership || {};
  const current = membership.current;

  const blocked = user.booking_blocked_until && new Date(user.booking_blocked_until) > new Date();

  const signupMethods = [
    user.googleId && 'Google',
    user.appleId && 'Apple',
    user.has_password && 'Email / Password',
  ].filter(Boolean);

  return (
    <>
      {/* ── Quick stats ── */}
      <div className="row g-4 mb-5">
        <StatTile label="Total Bookings" value={stats.total_bookings || 0} icon="bi-calendar-check" color="#4F97D8" />
        <StatTile label="Completed"      value={stats.completed || 0}      icon="bi-check2-circle"  color="#059669" />
        <StatTile label="Cancelled"      value={stats.cancelled || 0}      icon="bi-x-circle"       color="#DC2626" />
        <StatTile label="Total Spent"    value={fmtMoney(stats.total_spent)} icon="bi-cash-coin"    color="#7C3AED" />
      </div>

      {/* ── Personal ── */}
      <Card title="Personal Information" icon="bi-person-vcard">
        <div className="row">
          <Field label="Full Name" value={user.name} />
          <Field label="Email"     value={user.email} />
          <Field label="Phone"     value={user.phone ? `${user.phone_code ? user.phone_code + ' ' : ''}${user.phone}` : null} />
          <Field label="Gender"    value={user.gender && <span className="text-capitalize">{user.gender}</span>} />
          <Field
            label="Date of Birth"
            value={user.dob ? <>{fmtDate(user.dob)} <span className="text-muted fs-7">({age(user.dob)})</span></> : null}
          />
          <Field label="Nationality" value={user.nationality} />
          <Field label="Emirate"     value={user.emirate} />
          <Field label="Height"      value={user.height ? `${user.height} cm` : null} />
          <Field label="Profession"  value={user.profession} />
        </div>
      </Card>

      {/* ── About / interests ── */}
      <Card title="About & Interests" icon="bi-chat-quote">
        <div className="row">
          <Field label="Interests" value={user.interests} col="col-12" />
          <Field label="About"     value={user.about} col="col-12" />
        </div>
      </Card>

      {/* ── Membership ── */}
      <Card
        title="Membership"
        icon="bi-award"
        toolbar={
          membership.pending_approvals > 0 ? (
            <span className="badge badge-light-warning">{membership.pending_approvals} pending approval</span>
          ) : null
        }
      >
        {current ? (
          <div className="row">
            <Field label="Current Plan" value={current.plan_title} />
            <Field
              label="Sessions"
              value={current.is_unlimited ? <span className="badge badge-light-info">Unlimited</span> : current.no_sessions}
            />
            <Field
              label="Plan State"
              badge={
                <span className={`badge ${current.is_active ? 'badge-light-success' : 'badge-light-secondary'}`}>
                  {current.is_active ? 'Active' : current.status || 'Inactive'}
                </span>
              }
            />
            <Field
              label="Approval"
              badge={
                <span className={`badge ${
                  current.approval_status === 'Approved' ? 'badge-light-success'
                    : current.approval_status === 'Rejected' ? 'badge-light-danger'
                    : 'badge-light-warning'
                }`}>
                  {current.approval_status || 'Approved'}
                </span>
              }
            />
            <Field label="Subscribed On"  value={fmtDateTime(current.subscribed_at)} />
            <Field label="Expires On"     value={fmtDateTime(current.expire_at)} />
            <Field label="Paid Amount"    value={fmtMoney(current.payment_amount)} />
            <Field label="Total Subscriptions" value={membership.total || 0} />
            <Field label="Lifetime Membership Spend" value={fmtMoney(membership.total_paid)} />
          </div>
        ) : (
          <div className="text-muted py-4 text-center">
            <i className="bi bi-award fs-2x d-block mb-2"></i>
            No membership subscribed yet
          </div>
        )}
      </Card>

      {/* ── Booking activity ── */}
      <Card title="Booking Activity" icon="bi-calendar-range">
        <div className="row">
          <Field label="Total Bookings" value={stats.total_bookings || 0} />
          <Field label="Pending"        value={stats.pending || 0} />
          <Field label="Confirmed"      value={stats.confirmed || 0} />
          <Field label="Completed"      value={stats.completed || 0} />
          <Field label="Cancelled"      value={stats.cancelled || 0} />
          <Field label="Total Spent"    value={fmtMoney(stats.total_spent)} />
          <Field label="Cancellation Charges" value={fmtMoney(stats.cancellation_charges)} />
          <Field label="Refunded"       value={fmtMoney(stats.refunded)} />
          <Field label="First Booking"  value={fmtDate(stats.first_booking_date)} />
          <Field label="Last Booking"   value={fmtDate(stats.last_booking_date)} />
        </div>
      </Card>

      {/* ── Account & access ── */}
      <Card title="Account & Access" icon="bi-shield-lock">
        <div className="row">
          <Field
            label="Account Status"
            badge={<span className={user.status ? 'user-badge-active' : 'user-badge-restricted'}>{user.status ? 'Active' : 'Restricted'}</span>}
          />
          <Field
            label="Phone Verified"
            badge={
              user.phone_verified_at
                ? <span className="badge badge-light-success">Verified — {fmtDate(user.phone_verified_at)}</span>
                : <span className="badge badge-light-danger">Not verified</span>
            }
          />
          <Field
            label="Booking Block"
            badge={
              blocked
                ? <span className="badge badge-light-danger">Blocked till {fmtDateTime(user.booking_blocked_until)}</span>
                : <span className="badge badge-light-success">No restriction</span>
            }
          />
          <Field label="Sign-in Methods" value={signupMethods.length ? signupMethods.join(', ') : null} />
          <Field label="Registered On" value={fmtDateTime(user.createdAt)} />
        </div>
      </Card>
    </>
  );
};

export default ProfileInfo;
